/**
 * sl-9qm.10.1: FormConfig Type Definitions
 *
 * Public form configuration for user-generated content in AppBases.
 * Includes DSGVO/GDPR consent, spam protection, and file upload limits.
 *
 * Stored in ViewConfig.settings.formConfig (Y.js backed).
 */
import { z } from 'zod';

// ─── Field Types ────────────────────────────────────────────────────

/** Available form field input types */
export type FormFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'email'
  | 'url'
  | 'date'
  | 'select'
  | 'multi-select'
  | 'checkbox'
  | 'file';

/** Validation rule for a form field */
export interface FormFieldValidation {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: string;
  patternMessage?: string;
}

/** Single form field configuration */
export interface FormFieldConfig {
  /** Database column ID this field maps to */
  columnId: string;
  /** Display label */
  label: string;
  /** Input type */
  type: FormFieldType;
  /** Placeholder text */
  placeholder?: string;
  /** Help text shown below the field */
  helpText?: string;
  /** Validation rules */
  validation?: FormFieldValidation;
  /** Options for select/multi-select fields */
  options?: string[];
  /** Default value */
  defaultValue?: unknown;
}

// ─── DSGVO / GDPR Consent ──────────────────────────────────────────

/** GDPR consent configuration (required for EU compliance) */
export interface FormConsentConfig {
  /** URL to the privacy policy (required!) */
  privacyPolicyUrl: string;
  /** Custom consent text shown with the checkbox */
  consentText: string;
  /** Whether explicit consent checkbox is required */
  requireExplicitConsent: boolean;
  /** Data retention notice text */
  dataRetentionNotice?: string;
}

// ─── Spam Protection ────────────────────────────────────────────────

/**
 * Spam protection configuration. Anonymous submissions are always proven by
 * the platform's single-use write proof; there is no per-form captcha.
 */
export interface SpamProtectionConfig {
  /** Enable honeypot field (invisible trap field) */
  honeypot: boolean;
  /** Rate limit: maximum submissions per visitor per hour */
  rateLimitPerHour: number;
}

// ─── File Upload Limits ─────────────────────────────────────────────

/** File upload constraints for form file fields */
export interface FileUploadLimits {
  /** Maximum file size in bytes */
  maxSizeBytes: number;
  /** Allowed MIME types (e.g. ['image/png', 'application/pdf']) */
  allowedMimeTypes: string[];
  /** Maximum number of files per submission */
  maxFiles: number;
}

// ─── Form Submission ────────────────────────────────────────────────

/** What happens after successful form submission */
export type SubmitAction =
  | { type: 'message'; message: string }
  | { type: 'redirect'; url: string };

/** Workflow automation triggered after a successful persisted submission */
export interface FormWorkflowTriggerConfig {
  /** Whether this trigger is active */
  enabled: boolean;
  /** AutomationConfig ID to run with the automation schedule actor */
  automationConfigId: string;
}

/** Internal workspace-only submit behavior for workflow editing forms */
export interface InternalFormSubmitConfig {
  /** Whether authenticated internal submission is active */
  enabled: boolean;
  /** Column IDs this form may update */
  editableColumnIds: string[];
  /** Workflow whose edit RBAC is required before writing */
  workflowId?: string;
}

// ─── Complete FormConfig ────────────────────────────────────────────

/** Complete public form configuration */
export interface FormConfig {
  /** Whether the form is enabled */
  enabled: boolean;
  /** Form title */
  title: string;
  /** Form description */
  description?: string;
  /** Configured form fields */
  fields: FormFieldConfig[];
  /** DSGVO/GDPR consent configuration */
  consent: FormConsentConfig;
  /** Spam protection settings */
  spamProtection: SpamProtectionConfig;
  /** File upload limits */
  fileUploadLimits: FileUploadLimits;
  /** Submit button label */
  submitLabel: string;
  /** Action after successful submission */
  submitAction: SubmitAction;
  /** Database doc ID for row creation */
  targetDatabaseDocId?: string;
  /** Database block ID for row creation */
  targetDatabaseBlockId?: string;
  /** Explicit workflow triggers fired after the submission row is persisted */
  workflowTriggers?: FormWorkflowTriggerConfig[];
  /** Internal-only update-row contract. Ignored by public submit route. */
  internalSubmit?: InternalFormSubmitConfig;
}

// ─── Zod Schemas ────────────────────────────────────────────────────

export const formFieldValidationSchema = z.object({
  required: z.boolean().optional(),
  minLength: z.number().int().min(0).optional(),
  maxLength: z.number().int().min(1).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  pattern: z.string().optional(),
  patternMessage: z.string().optional(),
});

export const formFieldConfigSchema = z.object({
  columnId: z.string().min(1),
  label: z.string().min(1),
  type: z.enum([
    'text',
    'textarea',
    'number',
    'email',
    'url',
    'date',
    'select',
    'multi-select',
    'checkbox',
    'file',
  ]),
  placeholder: z.string().optional(),
  helpText: z.string().optional(),
  validation: formFieldValidationSchema.optional(),
  options: z.array(z.string()).optional(),
  defaultValue: z.unknown().optional(),
});

export const formConsentConfigSchema = z.object({
  privacyPolicyUrl: z
    .string()
    .min(1)
    .refine(value => {
      if (/^\/(?!\/)/.test(value)) {
        return !/[\\\u0000-\u001f]/.test(value);
      }
      try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
      } catch {
        return false;
      }
    }, 'Privacy policy must be an app route or an HTTP(S) URL'),
  consentText: z.string().min(1),
  requireExplicitConsent: z.boolean().default(true),
  dataRetentionNotice: z.string().optional(),
});

export const spamProtectionConfigSchema = z.object({
  honeypot: z.boolean().default(true),
  rateLimitPerHour: z.number().int().min(1).max(1000).default(60),
});

export const fileUploadLimitsSchema = z.object({
  maxSizeBytes: z
    .number()
    .int()
    .min(1024)
    .max(100 * 1024 * 1024)
    .default(10 * 1024 * 1024), // 10 MB
  allowedMimeTypes: z
    .array(z.string())
    .default([
      'image/png',
      'image/jpeg',
      'image/gif',
      'image/webp',
      'application/pdf',
    ]),
  maxFiles: z.number().int().min(1).max(20).default(5),
});

export const submitActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('message'), message: z.string().min(1) }),
  z.object({ type: z.literal('redirect'), url: z.string().url() }),
]);

export const formWorkflowTriggerConfigSchema = z.object({
  enabled: z.boolean().default(false),
  automationConfigId: z.string().min(1),
});

export const internalFormSubmitConfigSchema = z.object({
  enabled: z.boolean().default(false),
  editableColumnIds: z.array(z.string().min(1)).default([]),
  workflowId: z.string().min(1).optional(),
});

export const formConfigSchema = z.object({
  enabled: z.boolean().default(false),
  title: z.string().min(1).default('Submit'),
  description: z.string().optional(),
  fields: z.array(formFieldConfigSchema).default([]),
  consent: formConsentConfigSchema,
  spamProtection: spamProtectionConfigSchema,
  fileUploadLimits: fileUploadLimitsSchema,
  submitLabel: z.string().min(1).default('Submit'),
  submitAction: submitActionSchema.prefault({
    type: 'message',
    message: 'Thank you for your submission!',
  }),
  targetDatabaseDocId: z.string().optional(),
  targetDatabaseBlockId: z.string().optional(),
  workflowTriggers: z.array(formWorkflowTriggerConfigSchema).default([]),
  internalSubmit: internalFormSubmitConfigSchema.optional(),
});

// ─── Defaults ───────────────────────────────────────────────────────

export const DEFAULT_FORM_CONSENT: FormConsentConfig = {
  privacyPolicyUrl: '',
  consentText:
    'I agree to the processing of my data in accordance with the privacy policy.',
  requireExplicitConsent: true,
};

export const DEFAULT_SPAM_PROTECTION: SpamProtectionConfig = {
  honeypot: true,
  rateLimitPerHour: 60,
};

export const DEFAULT_FILE_UPLOAD_LIMITS: FileUploadLimits = {
  maxSizeBytes: 10 * 1024 * 1024, // 10 MB
  allowedMimeTypes: [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'application/pdf',
  ],
  maxFiles: 5,
};

export const DEFAULT_FORM_CONFIG: FormConfig = {
  enabled: false,
  title: 'Submit',
  fields: [],
  consent: DEFAULT_FORM_CONSENT,
  spamProtection: DEFAULT_SPAM_PROTECTION,
  fileUploadLimits: DEFAULT_FILE_UPLOAD_LIMITS,
  submitLabel: 'Submit',
  submitAction: { type: 'message', message: 'Thank you for your submission!' },
  workflowTriggers: [],
};
